import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface CheckoutSession {
  id: string;
  status: string;
}

export const useMessagingCreditsCheckoutReturn = () => {
  const { t } = useTranslation('messaging');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const flag = searchParams.get('checkout');
    const sessionId = searchParams.get('sessionId');
    if (!flag && !sessionId) return;

    const finish = async () => {
      try {
        if (flag === 'cancel') {
          toast.info(t('credits.purchaseCancelled'));
          return;
        }

        if (!sessionId) {
          toast.info(t('credits.purchaseCheckHistory'));
          return;
        }

        const session = await api.post<CheckoutSession>(
          `/messaging/credits/checkout-sessions/${sessionId}/verify`,
          {},
        );

        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'ledger'] });
        await queryClient.invalidateQueries({
          queryKey: ['messaging', 'credits', 'checkout-sessions'],
        });

        if (session.status === 'paid') {
          toast.success(t('credits.purchaseSuccess'));
        } else if (session.status === 'processing' || session.status === 'pending') {
          toast.info(t('credits.purchasePending'));
        } else if (session.status === 'cancelled') {
          toast.info(t('credits.purchaseCancelled'));
        } else {
          toast.error(t('credits.purchaseFailed'));
        }
      } catch (err) {
        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
        await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'ledger'] });
        await queryClient.invalidateQueries({
          queryKey: ['messaging', 'credits', 'checkout-sessions'],
        });
        toast.error(
          err instanceof ApiError ? tErrors(err.code, err.params) : t('credits.purchaseFailed'),
        );
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        next.delete('sessionId');
        setSearchParams(next, { replace: true });
      }
    };

    void finish();
  }, [queryClient, searchParams, setSearchParams, t]);
};
